"use client";

import React from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import Link from "next/link";
import { useFetchNewsArticles } from "@/apis/queryFunctions/news";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useDomSlider } from "@/hooks/useAutoSlider";
import SectionTitle from "@/components/SectionTitle";

interface NewsSectionProps {
  locale: string;
  title?: string;
  subTitle?: string;
}

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateString;
  }
};

/* =========================================================================
   SECTION 08: TIN TỨC & SẢN PHẨM / SỰ KIỆN
   - Mapped to ScrollHeader menu item '08: Tin tức'
   - Triggers anchor element #tin-tuc
   ========================================================================= */
export default function NewsSection({
  locale,
  title,
  subTitle,
}: NewsSectionProps) {
  const { containerRef, isDraggingRef, scrollPrev, scrollNext, sliderEvents } =
    useDomSlider();

  const { data: newsItems = [], isLoading } = useFetchNewsArticles({
    limit: 10,
    condition: { ngon_ngu: locale },
  });
  console.log(newsItems);

  if (isLoading) {
    return (
      <section
        id="tin-tuc"
        className="py-12 md:py-16 bg-white border-t border-black/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex justify-center items-center py-12">
          <div className="w-12 h-12 border-4 border-primaryHover border-t-transparent rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  if (!newsItems || newsItems.length === 0) {
    return null;
  }

  return (
    <section
      id="tin-tuc"
      className="py-12 md:py-16 bg-transparent border-t border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Title */}
        {/* Title */}
        <SectionTitle
          title={title || "TIN TỨC & SỰ KIỆN"}
          subtitle={subTitle || "Bản tin dự án"}
          theme="green"
          subtitleColor="primaryHover"
          underlineColor="primaryHover"
          className="mb-16"
        />

        <div className="relative group/slider">
          {/* Left Navigation Arrow */}
          <button
            onClick={() => scrollPrev()}
            type="button"
            className="absolute -left-4 top-1/2 -translate-y-1/2 z-30 bg-white hover:bg-primaryHover hover:text-white text-primaryHover border border-primaryHover/20 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 opacity-0 group-hover/slider:opacity-100 focus:outline-none pointer-events-auto hidden sm:flex cursor-pointer"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Right Navigation Arrow */}
          <button
            onClick={() => scrollNext()}
            type="button"
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-30 bg-white hover:bg-primaryHover hover:text-white text-primaryHover border border-primaryHover/20 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 opacity-0 group-hover/slider:opacity-100 focus:outline-none pointer-events-auto hidden sm:flex cursor-pointer"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>

          <div
            ref={containerRef}
            {...sliderEvents}
            className="flex overflow-x-auto pb-1 gap-6 scrollbar-none snap-x snap-mandatory cursor-grab active:cursor-grabbing select-none w-full scroll-smooth [&::-webkit-scrollbar]:hidden"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {newsItems.map((item: any) => {
              const imageUrl = appendImageUrlFromAPI({
                src: item.picture || item.image,
                size: "xl",
              });
              return (
                <Link
                  key={item._id || item.id}
                  href={`/${locale}/tin-tuc/${item.slug}`}
                  onClick={(e) => {
                    if (isDraggingRef.current) {
                      e.preventDefault();
                    }
                  }}
                  className="w-[280px] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] shrink-0 snap-start bg-white rounded-2xl overflow-hidden border border-black/5 hover:border-primary/20 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col"
                >
                  {/* Cover image */}
                  <div className="relative w-full aspect-[16/10] bg-gray-50 overflow-hidden flex-shrink-0">
                    <SmoothImage
                      src={imageUrl || "/images/map_ora.png"}
                      alt={item.title}
                      fill
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 90vw"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* Metadata Content */}
                  <div className="p-6 flex flex-col flex-1 justify-between">
                    <div>
                      <div className="flex items-center gap-1.5 text-gray-400 text-[11px] font-bold mb-3 uppercase tracking-wider">
                        <Calendar size={12} />
                        <span>
                          {formatDate(item.date_created || item.date)}
                        </span>
                      </div>
                      <h3 className="text-sm sm:text-base font-extrabold text-primary group-hover:text-primaryHover transition duration-200 line-clamp-2 leading-snug mb-3">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed font-medium">
                        {item.summary ||
                          (item.content
                            ? item.content
                                .replace(/<[^>]*>/g, "")
                                .substring(0, 120) + "..."
                            : "")}
                      </p>
                    </div>
                    <span className="text-primaryHover font-bold text-xs uppercase tracking-wider mt-4 inline-block group-hover:translate-x-1.5 transition-transform duration-300">
                      Đọc chi tiết →
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
